/* eslint-disable react-refresh/only-export-components -- hook + provider share one module */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { notification } from "antd";
import PTSNavigationRoutes from "../utils/navigationRoutes";
import { toBasicHeader, toZulipUserId } from "../utils/zulipAuth";

type ZulipStreamsResponse = {
  result: string;
  msg: string;
  streams: Array<{ stream_id: number; name: string; invite_only?: boolean }>;
};

type ZulipRegisterResponse = {
  result: string;
  msg: string;
  queue_id: string;
  last_event_id: number;
};

type ZulipEventMessage = {
  id: number;
  sender_id: number;
  sender_full_name: string;
  content: string;
  timestamp: number;
  type: "stream" | "private";
  stream_id?: number;
  subject?: string;
  display_recipient?: string | Array<{ id: number; full_name: string }>;
};

type ZulipEvent = {
  id: number;
  type: string;
  message?: ZulipEventMessage;
};

type ZulipEventsResponse = {
  result: string;
  msg: string;
  events: ZulipEvent[];
};

type StreamRow = { id: string; streamId: number; name: string };

function isNewMessageToastEnabled(): boolean {
  const v = import.meta.env.VITE_PTS_CHAT_NEW_MESSAGE_TOAST_ENABLED as
    | string
    | undefined;
  if (v === undefined || v === "") {
    return true;
  }
  return !["false", "0", "no", "off"].includes(v.trim().toLowerCase());
}

function getNewMessageToastType(): "info" | "success" | "warning" | "error" {
  const v =
    (import.meta.env.VITE_PTS_CHAT_NEW_MESSAGE_TOAST_TYPE as string | undefined)
      ?.trim()
      .toLowerCase() ?? "info";
  if (v === "success" || v === "warning" || v === "error" || v === "info") {
    return v;
  }
  return "info";
}

function stripZulipContentPreview(html: string, max = 200): string {
  if (!html) {
    return "";
  }
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const t = doc.body.textContent?.replace(/\s+/g, " ").trim() ?? "";
    return t.length > max ? `${t.slice(0, max)}…` : t;
  } catch {
    return html
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, max);
  }
}

function formatZulipEventTime(timestamp: number): string {
  if (typeof timestamp !== "number" || timestamp <= 0) {
    return "";
  }
  try {
    return new Date(timestamp * 1000).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

type NewMessageNotifyParts = {
  sender: string;
  locationLine: string;
  preview: string;
  sentAt: string;
};

function buildNewMessageNotificationParts(
  message: ZulipEventMessage,
  conversationId: string,
  streams: StreamRow[],
  zulipUserId: number | null
): NewMessageNotifyParts {
  const sender = message.sender_full_name?.trim() || "Someone";
  const preview = stripZulipContentPreview(message.content || "");
  const sentAt = formatZulipEventTime(message.timestamp);

  if (message.type === "stream") {
    const ch = streams.find(s => s.id === conversationId);
    const streamName =
      ch?.name ??
      (typeof message.display_recipient === "string"
        ? message.display_recipient
        : "Channel");
    const topic = message.subject?.trim();
    const locationLine = topic
      ? `Channel  ·  #${streamName}  ·  ${topic}`
      : `Channel  ·  #${streamName}`;
    return { sender, locationLine, preview, sentAt };
  }

  const recipients = Array.isArray(message.display_recipient)
    ? message.display_recipient
    : [];
  const other = recipients.find(r => r.id !== zulipUserId);
  const fromOther = other?.full_name?.trim();
  const fromSingle =
    recipients.length === 1 ? recipients[0]?.full_name?.trim() : undefined;
  const peerName = fromOther ?? fromSingle;
  const locationLine = peerName
    ? `Direct message  ·  ${peerName}`
    : "Direct message";

  return { sender, locationLine, preview, sentAt };
}

function NewMessageNotificationBody({
  sender,
  locationLine,
  preview,
  sentAt,
}: NewMessageNotifyParts) {
  return (
    <div className="pts-zulip-new-msg-notify max-w-[min(100vw-2rem,22rem)] text-left text-white">
      <div className="text-[13px] font-semibold leading-snug text-white">
        {sender}
      </div>
      <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-white/75">
        {locationLine}
      </div>
      {sentAt ? (
        <div className="mt-0.5 text-[11px] tabular-nums text-white/55">
          {sentAt}
        </div>
      ) : null}
      {preview ? (
        <div className="mt-2 rounded-md border border-white/20 bg-white/20 px-2.5 py-2 text-[12px] leading-relaxed text-white/95 shadow-inner">
          <p className="line-clamp-4 whitespace-pre-wrap break-words">
            {preview}
          </p>
        </div>
      ) : (
        <p className="mt-2 text-[12px] italic text-white/55">
          (No text preview)
        </p>
      )}
    </div>
  );
}

function showNewMessageNotification(
  message: ZulipEventMessage,
  parts: NewMessageNotifyParts,
  toastType: ReturnType<typeof getNewMessageToastType>
) {
  const key = `pts-zulip-new-${message.id}`;
  const payload = {
    key,
    message: (
      <span className="text-sm font-semibold text-white">Team Chat</span>
    ),
    description: (
      <NewMessageNotificationBody
        sender={parts.sender}
        locationLine={parts.locationLine}
        preview={parts.preview}
        sentAt={parts.sentAt}
      />
    ),
    placement: "topRight" as const,
    duration: 5,
    className: "pts-zulip-chat-toast-notification",
    style: { width: 360 },
  };

  switch (toastType) {
    case "success":
      notification.success(payload);
      break;
    case "warning":
      notification.warning(payload);
      break;
    case "error":
      notification.error(payload);
      break;
    default:
      notification.info(payload);
  }
}

export type PtsZulipUnreadContextValue = {
  unreadByConversationId: Record<string, number>;
  totalZulipChatUnread: number;
  setActiveChatConversationId: (id: string | null) => void;
  clearConversationUnread: (id: string) => void;
  bumpUnread: (conversationId: string) => void;
};

const PtsZulipUnreadContext = createContext<PtsZulipUnreadContextValue | null>(
  null
);

function parseZulipAuthFromStorage(): unknown {
  const raw = localStorage.getItem("zulipAuth");
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return raw;
  }
}

function resolveConversationIdFromEventMessage(
  message: ZulipEventMessage,
  channels: StreamRow[],
  zulipUserId: number | null
): string | null {
  if (message.type === "stream") {
    const byStreamId =
      typeof message.stream_id === "number"
        ? channels.find(c => c.streamId === message.stream_id)
        : null;
    if (byStreamId) {
      return byStreamId.id;
    }
    const recipientName =
      typeof message.display_recipient === "string"
        ? message.display_recipient
        : null;
    if (recipientName) {
      const byName = channels.find(
        c => c.name.toLowerCase() === recipientName.toLowerCase()
      );
      return byName?.id ?? null;
    }
    return null;
  }

  const recipients = Array.isArray(message.display_recipient)
    ? message.display_recipient
    : [];
  const otherRecipient = recipients.find(
    recipient => recipient.id !== zulipUserId
  );
  const targetUserId =
    otherRecipient?.id ??
    (message.sender_id !== zulipUserId ? message.sender_id : null);
  if (!targetUserId) {
    return null;
  }
  return `dm-${targetUserId}`;
}

export function usePtsZulipUnread(): PtsZulipUnreadContextValue {
  const ctx = useContext(PtsZulipUnreadContext);
  if (!ctx) {
    throw new Error(
      "usePtsZulipUnread must be used within PtsZulipUnreadProvider"
    );
  }
  return ctx;
}

export function PtsZulipUnreadProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [unreadByConversationId, setUnreadByConversationId] = useState<
    Record<string, number>
  >({});

  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const streamsRef = useRef<StreamRow[]>([]);
  const zulipUserIdRef = useRef<number | null>(null);
  const activeChatConversationIdRef = useRef<string | null>(null);
  const stopRef = useRef(false);
  const sessionRef = useRef(0);

  const setActiveChatConversationId = useCallback((id: string | null) => {
    activeChatConversationIdRef.current = id?.trim() ? id : null;
  }, []);

  const clearConversationUnread = useCallback((id: string) => {
    if (!id) {
      return;
    }
    setUnreadByConversationId(prev => {
      if (!(id in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const bumpUnread = useCallback((conversationId: string) => {
    if (!conversationId) {
      return;
    }
    setUnreadByConversationId(prev => ({
      ...prev,
      [conversationId]: (prev[conversationId] ?? 0) + 1,
    }));
  }, []);

  const totalZulipChatUnread = useMemo(
    () =>
      Object.values(unreadByConversationId).reduce(
        (sum, n) => sum + (typeof n === "number" ? n : 0),
        0
      ),
    [unreadByConversationId]
  );

  useEffect(() => {
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL as string | undefined;
    if (!baseUrl) {
      return;
    }

    stopRef.current = false;
    const sessionId = ++sessionRef.current;

    const fetchStreams = async (authHeader: string) => {
      try {
        const res = await fetch(`${baseUrl}/streams`, {
          headers: { Authorization: authHeader },
        });
        if (!res.ok) {
          return;
        }
        const data = (await res.json()) as ZulipStreamsResponse;
        if (!Array.isArray(data.streams)) {
          return;
        }
        streamsRef.current = data.streams.map(s => ({
          id: `ch-${s.stream_id}`,
          streamId: s.stream_id,
          name: s.name,
        }));
      } catch {
        /* ignore */
      }
    };

    const tryBumpForNewMessage = (
      message: ZulipEventMessage,
      conversationId: string
    ) => {
      if (
        zulipUserIdRef.current !== null &&
        message.sender_id === zulipUserIdRef.current
      ) {
        return;
      }
      const onChatPage =
        pathnameRef.current === PTSNavigationRoutes.CHAT ||
        pathnameRef.current.endsWith(PTSNavigationRoutes.CHAT);
      if (
        onChatPage &&
        conversationId === activeChatConversationIdRef.current
      ) {
        return;
      }
      bumpUnread(conversationId);
      if (!isNewMessageToastEnabled()) {
        return;
      }
      const parts = buildNewMessageNotificationParts(
        message,
        conversationId,
        streamsRef.current,
        zulipUserIdRef.current
      );
      const toastType = getNewMessageToastType();
      showNewMessageNotification(message, parts, toastType);
    };

    const startPolling = async () => {
      while (!stopRef.current && sessionId === sessionRef.current) {
        const parsed = parseZulipAuthFromStorage();
        const authHeader = toBasicHeader(parsed);
        const zulipId = toZulipUserId(parsed);
        if (!authHeader) {
          await new Promise(r => setTimeout(r, 3000));
          continue;
        }
        zulipUserIdRef.current = zulipId;
        await fetchStreams(authHeader);

        const registerPayload = new URLSearchParams();
        registerPayload.set("event_types", JSON.stringify(["message"]));

        let registerResponse: Response;
        try {
          registerResponse = await fetch(`${baseUrl}/register`, {
            method: "POST",
            headers: {
              Authorization: authHeader,
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: registerPayload.toString(),
          });
        } catch {
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }

        if (!registerResponse.ok || stopRef.current) {
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }

        let registerData: ZulipRegisterResponse;
        try {
          registerData =
            (await registerResponse.json()) as ZulipRegisterResponse;
        } catch {
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }

        if (registerData.result !== "success" || !registerData.queue_id) {
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }

        let lastEventId = registerData.last_event_id;

        while (!stopRef.current && sessionId === sessionRef.current) {
          const reRead = parseZulipAuthFromStorage();
          const currentAuth = toBasicHeader(reRead);
          if (!currentAuth) {
            break;
          }

          const eventsUrl =
            `${baseUrl}/events?queue_id=${encodeURIComponent(registerData.queue_id)}` +
            `&last_event_id=${encodeURIComponent(String(lastEventId))}`;

          let eventsResponse: Response;
          try {
            eventsResponse = await fetch(eventsUrl, {
              method: "GET",
              headers: { Authorization: currentAuth },
            });
          } catch {
            await new Promise(r => setTimeout(r, 1500));
            continue;
          }

          if (!eventsResponse.ok) {
            const retryDelay = eventsResponse.status === 429 ? 2500 : 1200;
            await new Promise(r => setTimeout(r, retryDelay));
            break;
          }

          let eventsData: ZulipEventsResponse;
          try {
            eventsData = (await eventsResponse.json()) as ZulipEventsResponse;
          } catch {
            await new Promise(r => setTimeout(r, 1200));
            continue;
          }

          if (
            !Array.isArray(eventsData.events) ||
            eventsData.events.length === 0
          ) {
            await new Promise(r => setTimeout(r, 350));
            continue;
          }

          for (const event of eventsData.events) {
            lastEventId = Math.max(lastEventId, event.id);
            if (event.type !== "message" || !event.message) {
              continue;
            }
            const uid = zulipUserIdRef.current ?? toZulipUserId(reRead);
            zulipUserIdRef.current = uid;
            const conversationId = resolveConversationIdFromEventMessage(
              event.message,
              streamsRef.current,
              uid
            );
            if (!conversationId) {
              continue;
            }
            tryBumpForNewMessage(event.message, conversationId);
          }
        }
      }
    };

    void startPolling();

    return () => {
      stopRef.current = true;
      sessionRef.current += 1;
    };
  }, [bumpUnread]);

  const value = useMemo<PtsZulipUnreadContextValue>(
    () => ({
      unreadByConversationId,
      totalZulipChatUnread,
      setActiveChatConversationId,
      clearConversationUnread,
      bumpUnread,
    }),
    [
      unreadByConversationId,
      totalZulipChatUnread,
      setActiveChatConversationId,
      clearConversationUnread,
      bumpUnread,
    ]
  );

  return (
    <PtsZulipUnreadContext.Provider value={value}>
      {children}
    </PtsZulipUnreadContext.Provider>
  );
}
